import { DataType, FormDialogMode, SelectOption, useNotify } from '@globaleris/ui.library'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity } from '../../../../../api'
import { ProductPriceModel } from '../../../../../api/Argentia/endpoints/products/model/products'
import { EnumTipoCPrecio } from '../../../../../api/oData/enums/enumTipoCPrecio'
import { StandardCard } from '../../../../../components/shared/standardCard/standardCard'
import { enumToSelectOptionArray } from '../../../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../../../hooks'
import { mapPriceComponentTypesToSelectOption } from '../../../products.mapper'
import { AddProductPriceForm } from '../../../products.Model'
import { ProductPriceTable } from '../productPriceTable/productPriceTable'

interface ProductPriceCardProps {
  isLoading: boolean
  commodity: EnumCommodity
  data: ProductPriceModel[]
  priceComponentTypeOfertaOptions: SelectOption[]
  priceComponentTypesContratoOptions: SelectOption[]
  pctContratoOptionsIsLoading: boolean
  onRowInsert: (formData: AddProductPriceForm) => void
  onRowEdit: (formData: AddProductPriceForm) => void
  onRowDelete: (selectedRow: ProductPriceModel) => void
  onFinalRelationClick?: () => void
}

export const ProductPriceCard: React.FC<ProductPriceCardProps> = ({
  isLoading,
  commodity,
  data,
  priceComponentTypeOfertaOptions,
  priceComponentTypesContratoOptions,
  pctContratoOptionsIsLoading,
  onRowInsert,
  onRowEdit,
  onRowDelete,
  onFinalRelationClick = () => {}
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'products', 'enums'])
  const {
    openForm,
    updateFormData,
    updateFormDefinition,
    closeForm,
    formProps,
    CustomFormDialog,
    setFormWaiting
  } = useFormDialog()
  const { error } = useNotify()
  const [finalRelationIsSelected, setFinalRelationIsSelected] = React.useState<boolean>(false)
  const [editingPriceComponentTypeOption, setEditingPriceComponentTypeOption] =
    React.useState<SelectOption>()
  const [editingPriceContractOption, setEditingPriceContractOption] = React.useState<SelectOption>()

  const noData = data && data.length === 0

  // #region Handlers Starts
  const handleAddForm = () => {
    openForm({
      definition: AddProductPriceFormDefinition,
      title: t('products:forms.productPrice.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: {
        commodity
      },
      onClose: onAddFormCloseHandler
    })
  }

  const handleEditForm = (selectedRow: ProductPriceModel) => {
    setEditingPriceComponentTypeOption(mapPriceComponentTypesToSelectOption(selectedRow.price))
    setEditingPriceContractOption(
      selectedRow.priceContract
        ? mapPriceComponentTypesToSelectOption(selectedRow.priceContract)
        : undefined
    )
    if (selectedRow.type === EnumTipoCPrecio.Final) {
      setFinalRelationIsSelected(true)
      onFinalRelationClick()
    } else {
      setFinalRelationIsSelected(false)
    }
    openForm({
      definition: AddProductPriceFormDefinition,
      title: t('products:forms.productPrice.editTitle'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: {
        id: selectedRow.id,
        commodity,
        priceComponentTypeId: selectedRow.price.id,
        type: selectedRow.type,
        priceContractId: selectedRow.priceContract?.id,
        regenerate: selectedRow.regenerate ?? false
      },
      onClose: onEditFormCloseHandler
    })
  }

  const onAddFormCloseHandler = (formData: AddProductPriceForm) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(AddProductPriceFormDefinition.sections!, formData)) {
        error(t('products:forms.productPrice.notifications.incompleteForm'))
        return
      }
      onRowInsert(formData)
    }
    closeForm()
    setFinalRelationIsSelected(false)
    setEditingPriceComponentTypeOption(undefined)
    setEditingPriceContractOption(undefined)
  }

  const onEditFormCloseHandler = (formData: AddProductPriceForm) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(AddProductPriceFormDefinition.sections!, formData)) {
        error(t('products:forms.productPrice.notifications.incompleteForm'))
        return
      }
      onRowEdit(formData)
    }
    closeForm()
    setFinalRelationIsSelected(false)
    setEditingPriceComponentTypeOption(undefined)
    setEditingPriceContractOption(undefined)
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const mergeSelectOptionIfMissing = (
    options: SelectOption[],
    extraOption?: SelectOption
  ): SelectOption[] => {
    if (!extraOption || options.some(option => option.value === extraOption.value)) {
      return options
    }
    return [...options, extraOption]
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const AddProductPriceFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD',
        sections: [
          {
            inputs: [
              {
                name: 'commodity',
                label: t('products:forms.productPrice.inputs.commodity'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
                readOnly: true
              },
              {
                name: 'priceComponentTypeId',
                label: t('products:forms.productPrice.inputs.priceComponentType'),
                type: DataType.SELECT,
                selectOptions: mergeSelectOptionIfMissing(
                  priceComponentTypeOfertaOptions,
                  editingPriceComponentTypeOption
                ),
                required: true
              },
              {
                name: 'type',
                label: t('products:forms.productPrice.inputs.type'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(EnumTipoCPrecio, 'enums:EnumTipoCPrecio'),
                required: true,
                onChange: (value: string | number, formState) => {
                  if (value === EnumTipoCPrecio.Final) {
                    setFinalRelationIsSelected(true)
                    updateFormData(formState)
                    onFinalRelationClick()
                  } else {
                    setFinalRelationIsSelected(false)
                    updateFormData({ ...formState, priceContractId: null })
                  }
                }
              },
              {
                name: 'priceContractId',
                label: t('products:forms.productPrice.inputs.priceContractCodeDesc'),
                type: DataType.SELECT,
                selectOptions: mergeSelectOptionIfMissing(
                  priceComponentTypesContratoOptions,
                  editingPriceContractOption
                ),
                isLoading: pctContratoOptionsIsLoading,
                required: finalRelationIsSelected,
                display: finalRelationIsSelected
              },
              {
                name: 'regenerate',
                label: t('products:forms.productPrice.inputs.regenerate'),
                type: DataType.BOOLEAN,
                defaultValue: false,
                display: true
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      priceComponentTypeOfertaOptions,
      priceComponentTypesContratoOptions,
      pctContratoOptionsIsLoading,
      finalRelationIsSelected,
      editingPriceComponentTypeOption,
      editingPriceContractOption
    ])
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    updateFormDefinition(AddProductPriceFormDefinition)
  }, [AddProductPriceFormDefinition])

  // #endregion Hooks Ends

  return (
    <>
      <StandardCard
        isLoading={isLoading}
        showEmptyContent={noData}
        emptyCaption={t('products:components.productPriceComponents.emptyCaption')}
        emptyButtonCallback={handleAddForm}
        emptyButtonCaption={t('products:components.productPriceComponents.mainActionCard')}
      >
        <ProductPriceTable
          data={data}
          onRowInsert={handleAddForm}
          onRowEdit={handleEditForm}
          onRowDelete={onRowDelete}
        />
      </StandardCard>
      <CustomFormDialog {...formProps} />
    </>
  )
}
